import {
  NgSignaturePadOptions,
  SignaturePadComponent,
} from "@almothafar/angular-signature-pad";
import {
  Component,
  ViewChild,
  HostListener,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { DataElement, Doctor } from "src/app/shared/models/common";
import {
  Order,
  OrderLine,
  RXForm,
  RXLine,
  RXNote,
  RXOpt,
  RXOptGroup,
  RXOptValue,
  RXPdf,
  RXTooth,
  RX_NOTE_IMPORT_DETAILS,
  isFreeRxNote,
} from "src/app/shared/models/order.model";
import { BroshService } from "src/app/shared/services/brosh.service";
import { RealtimeService } from "src/app/shared/services/realtime.service";
import { ToasterService } from "src/app/shared/services/toaster.service";
import { RxLockService } from "src/app/shared/services/rx-lock.service";
import { DataService } from "src/app/shared/services/data.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { CommonModule, Location } from "@angular/common";
import html2canvas from "html2canvas";
import jspdf from "jspdf";
import { TeethEditComponent } from "./teeth-edit/teeth-edit.component";
import { Subject, forkJoin } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { map, scan, take, takeUntil } from "rxjs/operators";
import { DrawAreaComponent } from "./draw-area/draw-area.component";
import { AuthService } from "src/app/shared/services/auth.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { routeEnum, RouteEnum } from "src/app/shared/models/routes.model";
import { Store } from "@ngrx/store";
import {
  RELOAD_CURRENT_ORDER,
  PATCH_INBOX_ORDER,
  MARK_INBOX_NEEDS_RELOAD,
  RELOAD_INBOX,
  SELECT_INBOX_ORDER,
  SELECT_RX_FORM,
  UI__REQUEST_SET_LANG,
  UI__ROUTE_NAVIGATION,
  UI__SET_LANG,
} from "src/app/shared/ngrx/ngrx-actions";
import {
  curRXForm,
  currentOrder,
  doctorList,
  qcModeSw,
  rxItems,
  rxOpt,
} from "src/app/shared/ngrx/ngrx-selectors";
import { SelectModule } from "primeng/select";
import { LoaderComponent } from "src/app/shared/components/ui/loader/loader.component";
import { FormsModule } from "@angular/forms";
import { TooltipModule } from "primeng/tooltip";
import { PdfViewerComponent } from "./pdf-viewer/pdf-viewer.component";
import { InputWrapComponent } from "src/app/shared/components/ui/input-wrap.component";
import { RxOptDisplayComponent } from "src/app/shared/components/rx-opt-display/rx-opt-display.component";
import { RxImplantsComponent } from "./rx-implants/rx-implants.component";
import { SpinnerComponent } from "src/app/shared/components/ui/spinner/spinner.component";
import { OrderFilesComponent } from "../order-files/order-files.component";
import { SelectFixedNoteComponent } from "./select-fixed-note/select-fixed-note.component";
import { ModalPopupComponent } from "src/app/shared/components/ui/modal-popup.component";
import { OrderLinesComponent } from "../order-lines/order-lines.component";
import { ShilaPoDialogComponent } from "./shila-po-dialog/shila-po-dialog.component";
import { IntakePartyDialogComponent, isDuplicatesPassed } from "./intake-party-dialog/intake-party-dialog.component";
import { ImportDetailsNoteComponent } from "./import-details-note/import-details-note.component";
import { ofType, Actions } from "@ngrx/effects";
import { Clinic } from "src/app/shared/models/clinic.model";
import { clinicList } from "src/app/shared/ngrx/ngrx-selectors";

@Component({
  selector: "rx-form",
  templateUrl: "./rx-form.component.html",
  styleUrls: ["./rx-form.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    SelectModule,
    TranslateModule,
    LoaderComponent,
    FormsModule,
    TooltipModule,
    PdfViewerComponent,
    InputWrapComponent,
    RxOptDisplayComponent,
    TeethEditComponent,
    RxImplantsComponent,
    DrawAreaComponent,
    SpinnerComponent,
    OrderFilesComponent,
    SelectFixedNoteComponent,
    ModalPopupComponent,
    OrderLinesComponent,
    ShilaPoDialogComponent,
    IntakePartyDialogComponent,
    ImportDetailsNoteComponent,
  ],
})
export class RxFormComponent {
  @ViewChild("signature")
  public signaturePad: SignaturePadComponent;

  @ViewChild(TeethEditComponent)
  teethEditCmp: TeethEditComponent;

  @ViewChild(DrawAreaComponent)
  drawAreaCmp: DrawAreaComponent;

  loading: boolean;
  loadingIcons: boolean = true;
  icons: string[] = [];
  iconsLoaded: number = 0;
  saving: boolean;
  addFixedNoteOpenSw: boolean;
  canvasData: any;
  bgImageRatio = 1.1;
  bgDataUrl: any;
  pdf: RXPdf;
  dirty: boolean = false;
  showOrderLines: boolean;
  orderLines: OrderLine[] = [];
  destroy$ = new Subject();

  displayMergeRxPopup: boolean = false;
  displayShilaPoPopup: boolean = false;
  displayIntakePartyPopup = false;
  // The order whose popup the taker closed or finished: a reload of it does not reopen the popup.
  intakePartyClosedFor: number | null = null;
  // a merge asked for in the intake popup is running
  intakeMerging = false;
  // the order whose possible duplicates were looked up for a case with nothing to ask: once per order
  private intakeDuplicatesCheckedFor: number | null = null;
  displayGiveBackPopup: boolean = false;
  shilaPoEligible: boolean = false;
  // the imported POs of this order seen RESERVED by the last eligibility load, to notice one leaving the vault
  private reservedShilaPos = new Set<string>();
  private reservedShilaPosOrderId: number | null = null;
  mergeOrderIdMmd: number;

  get currentOrder(): Order {
    return this.store.selectSignal(currentOrder)();
  }

  get form(): RXForm {
    return this.store.selectSignal(curRXForm)();
  }

  get notes(): RXNote[] {
    return this.form.notes;
  }

  readonly isFreeNote = isFreeRxNote;

  // what an imported case carried that the RX has no place for: shown apart, below the free notes, never edited
  get importDetailsNotes(): RXNote[] {
    return this.form.notes.filter((n) => n.kind === RX_NOTE_IMPORT_DETAILS);
  }

  get doctorList(): Doctor[] {
    return this.store.selectSignal(doctorList)();
  }

  get optionGroups(): RXOptGroup[] {
    return this.form.rxOptions.filter(
      (o) =>
        o.optGroup != "IMP_ATTACH" &&
        o.optGroup != "SHADE" &&
        o.optGroup != "PATIENT" &&
        o.optGroup != "PROPERTIES" &&
        o.optGroup != "SCAN_DETAILS"
    );
  }

  get patientOpt(): RXOptGroup {
    return this.form.rxOptions.find((o) => o.optGroup == "PATIENT");
  }

  get propertiesOpt(): RXOptGroup {
    return this.form.rxOptions.find((o) => o.optGroup == "PROPERTIES");
  }

  get scanOpt(): RXOptGroup {
    return this.form.rxOptions.find((o) => o.optGroup == "SCAN_DETAILS");
  }

  get shade0(): RXOpt {
    return this.form.rxOptions
      .find((o) => o.optGroup == "SHADE")
      ?.options.find((o) => o.id == "OCCLUSAL_STAINING");
  }

  get shade1(): RXOpt {
    return this.form.rxOptions
      .find((o) => o.optGroup == "SHADE")
      ?.options.find((o) => o.id == "GINGIVAL_SHADE");
  }

  get shade2(): RXOpt {
    return this.form.rxOptions
      .find((o) => o.optGroup == "SHADE")
      ?.options.find((o) => o.id == "CENTRAL_SHADE");
  }

  get shade3(): RXOpt {
    return this.form.rxOptions
      .find((o) => o.optGroup == "SHADE")
      ?.options.find((o) => o.id == "OCCLUSAL_SHADE");
  }

  get implants(): RXTooth[] {
    return this.form.implantTeeth.filter((t) => !t.dirtySw);
  }

  // Set from pressing Give back: its FREE push is not an idle release.
  private givingBack = false;

  // The DB says who holds an intake case; only its taker may change it.
  get intakeMine(): boolean {
    return (
      !!this.form.intake &&
      this.form.intake.takerUserId === this.authService.currentUser.userId
    );
  }

  get intakePartyAsks(): boolean {
    const p = this.form.intake?.party;
    return this.intakeMine && !!p && (p.needsClinic || p.needsDentist || p.shilaDentist);
  }

  get intakePartyWaits(): boolean {
    const p = this.form.intake?.party;
    return this.intakeMine && !this.intakePartyAsks && !!p && (p.waitingClinic || p.waitingDentist);
  }

  // "Syncing with Meimad" beside the title, once (an intake case waiting for Meimad already says it)
  get mmdSyncNotice(): boolean {
    return this.form.mmdSyncPending && !this.intakePartyWaits;
  }

  get intakeReadOnly(): boolean {
    return !!this.form.intake && !this.intakeMine;
  }

  get readOnly(): boolean {
    return (
      this.form.statusCode == "SIGNED" ||
      this.form.statusCode == "FINAL" ||
      this.intakeReadOnly ||
      this.lockReadOnly
    );
  }

  // The short edit lock: someone else holds this draft, or it changed while
  // our lock had lapsed.
  get lockState() {
    return this.form.intake || this.form.statusCode != "DRAFT"
      ? null
      : this.rxLock.state();
  }

  get lockReadOnly(): boolean {
    return !!this.lockState && !this.lockState.editable;
  }

  // Why the lock leaves the draft read-only, when there is something to say;
  // otherwise the header shows the usual updated-at / by line.
  get lockNoticeKey(): string | null {
    const s = this.lockState;
    if (!s || s.editable) return null;
    if (s.lockedBy != null) return "translate.rx.rxLockedBy";
    if (s.idle) return "translate.rx.lockIdle";
    if (s.stale) return "translate.rx.lockStale";
    return null;
  }

  get displayDoctor(): boolean {
    return this.authService.currentUser.roleCode != "DOCTOR";
  }

  get displayClinic(): boolean {
    return this.authService.currentUser.roleCode == "DOCTOR";
  }

  get displayRXFiles(): boolean {
    return (
      this.authService.currentUser.isPlantUser ||
      this.authService.currentUser.isLabUser
    );
  }

  get allowSave(): boolean {
    return (
      !this.readOnly &&
      this.authService.currentUser.org == "LAB" &&
      this.currentOrder.statusCode != "DRAFT"
    );
  }

  get allowSign(): boolean {
    return (
      !this.readOnly &&
      !this.form.mmdSyncPending &&
      (this.authService.currentUser.org == "LAB" ||
        this.authService.currentUser.org == "CLINIC")
    );
  }

  // Not while the signed RX waits for Meimad's number: its PDF is made then, from the RX as signed.
  get allowChangeToDraft(): boolean {
    return this.form.allowChangeToDraft && !this.form.mmdSyncPending;
  }

  get allowDelete(): boolean {
    return this.form.statusCode == "DRAFT" && !this.form.intake && !this.readOnly;
  }

  get allowApproveToMmd(): boolean {
    return (
      this.authService.currentUser.org == "LAB" &&
      this.form.statusCode == "SIGNED" &&
      this.form.type == "CLINIC" &&
      !this.form.intake
    );
  }

  // Cancel duplicate and Import Shila PO: today's approve-stage forms, plus
  // an intake form for the user who holds it.
  get allowOrderActions(): boolean {
    return (
      this.allowApproveToMmd ||
      (this.intakeMine && this.authService.currentUser.org == "LAB")
    );
  }

  get allowUploadClinicFiles(): boolean {
    if (this.authService.currentUser.org == "LAB") {
      return true;
    } else {
      return !this.readOnly;
    }
  }

  get qcModeSw(): boolean {
    return this.store.selectSignal(qcModeSw)();
  }

  get signTooltip(): string {
    if (this.signEnabled()) {
      return null;
    }
    if (!this.form.lines.length) {
      return;
      this.translateService.instant("translate.validation.selectRxItems");
    } else {
      return this.translateService.instant(
        "translate.validation.fillRequiredFields"
      );
    }
  }

  get clinicList(): Clinic[] {
    return this.store.selectSignal(clinicList)();
  }

  constructor(
    private readonly store: Store,
    private broshService: BroshService,
    private sharedService: SharedService,
    private dataService: DataService,
    private location: Location,
    private authService: AuthService,
    private translateService: TranslateService,
    private router: Router,
    private actions$: Actions,
    private rxLock: RxLockService,
    private realtime: RealtimeService,
    private toaster: ToasterService
  ) { }

  ngAfterViewInit(): void { }

  ngOnDestroy(): void {
    this.destroy$.next(1);

    this.store.dispatch(
      SELECT_RX_FORM({
        form: null,
      })
    );
  }

  ngOnInit(): void {
    this.actions$
      .pipe(
        ofType(UI__SET_LANG),
        takeUntil(this.destroy$),
        map(() => {
          this.broshService.getRXItems().subscribe();
        })
      )
      .subscribe();

    this.listenForIntakeNudges();
    this.loadData();
    this.loadIcons();
  }

  // A read-only view of an intake case reloads when the holder lets go of it
  // or it is accepted or cancelled; a repeated nudge only reloads again.
  listenForIntakeNudges() {
    this.realtime.nudges$.pipe(takeUntil(this.destroy$)).subscribe((n) => {
      // The server made the RX PDF once Meimad's number arrived: the header shows the number, and the form its PDF
      // without the pending notice (unsaved edits of another form of the order stay; the header still follows).
      if (n.type === "RX_PDF") {
        if (this.currentOrder?.orderId === n.orderId) {
          this.reloadOrderHeader(false);
          if (!this.dirty) {
            this.getFormData();
          }
        }
        return;
      }
      // The on-prem box reported on this order's Shila POs: the header and the Import PO button follow.
      if (n.type === "SHILA_PO") {
        if (this.currentOrder && n.orderIds?.includes(this.currentOrder.orderId)) {
          this.reloadOrderHeader(false);
          this.loadShilaPoEligibility();
        }
        return;
      }
      // The case was freed while its taker has it open: the idle timeout. The taker's
      // own give-back also pushes FREE, so once they have pressed it (givingBack
      // stays set until the reply fails) the push is not an idle release.
      if (
        n.type === "INTAKE_CASE" &&
        n.state === "FREE" &&
        this.currentOrder?.orderId === n.orderId &&
        this.intakeMine &&
        !this.givingBack
      ) {
        this.getFormData();
        this.sharedService.alertInfo(
          this.translateService.instant("translate.rx.intakeReleasedIdle")
        );
        return;
      }
      // The clinic or dentist answer arrived or was filed: the form follows, unless edits are unsaved.
      if (n.type === "INTAKE_CASE" && n.state === "UPDATED" && this.currentOrder?.orderId === n.orderId) {
        this.reloadFormKeepingEdits();
        return;
      }
      if (
        n.type === "INTAKE_CASE" &&
        ["FREE", "ACCEPTED", "CANCELLED"].includes(n.state) &&
        this.currentOrder?.orderId === n.orderId &&
        this.intakeReadOnly
      ) {
        this.getFormData();
      }
    });
  }

  loadIcons() {
    SharedService.teethDef.forEach((t) => {
      this.icons.push("assets/img/teeth/dirty/" + t.id + ".svg");
      this.icons.push("assets/img/teeth/default/" + t.id + ".svg");
      this.icons.push("assets/img/teeth/selected/" + t.id + ".svg");
    });

    for (let i = 0; i < this.icons.length; i++) {
      let img = new Image();
      img.onload = () => {
        this.onIconLoaded();
      };
      img.src = this.icons[i];
    }
  }

  onIconLoaded() {
    this.iconsLoaded++;
    if (this.icons.length == this.iconsLoaded) {
      this.loadingIcons = false;
    }
  }

  loadData() {
    this.loading = true;

    let rxOptions = this.store.selectSignal(rxOpt)();
    let rxItem = this.store.selectSignal(rxItems)();

    if (rxOptions.length == 0 || rxItem.length == 0) {
      let sources = [
        this.dataService.getRXOpt(),
        this.broshService.getRXItems(),
      ];
      forkJoin(sources).subscribe(
        ([v1, v2]) => {
          this.getFormData();
        },
        (error) => {
          this.sharedService.alertError(error);
          this.location.back();
        }
      );
    } else {
      rxOptions.forEach((o) => {
        o.resetValue();
      });
      this.getFormData();
    }
  }

  getFormData() {
    if (!this.currentOrder) return;

    this.broshService
      .getRXForm(this.currentOrder.orderId, this.form.formId)
      // a form that left the screen meanwhile is not put back in the store
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.store.dispatch(
            SELECT_RX_FORM({
              form: data,
            })
          );
          this.postFormLoad();
          this.patchHeaderIntake(data.intake);
          this.dirty = false;
          this.loading = false;
          this.loadShilaPoEligibility();
          if (
            this.intakePartyAsks &&
            this.intakePartyClosedFor !== this.currentOrder.orderId &&
            !this.displayIntakePartyPopup &&
            !this.displayShilaPoPopup
          ) {
            this.openIntakeParty();
          } else {
            this.offerIntakeDuplicates();
          }
          if (this.holdsLock(data)) {
            this.rxLock.start(data.formId, data.updDate as any);
          } else {
            this.rxLock.stop();
          }
        },
        (error) => {
          this.loading = false;
          this.sharedService.alertError(error);
          this.location.back();
        }
      );
  }

  // The order header shows the taker from the order's intake state, so it
  // follows every form load (the take, a nudge, an idle release, an acceptance)
  // and the give-back. A freed or accepted case keeps its intake state without
  // a taker, as the inbox rows do.
  private patchHeaderIntake(intake: RXForm["intake"]): void {
    if (!this.currentOrder) return;
    const held = intake ?? this.currentOrder.intake;
    this.store.dispatch(
      PATCH_INBOX_ORDER({
        orderId: this.currentOrder.orderId,
        changes: {
          intake: intake ?? (held ? { ...held, takerUserId: null, takerName: null } : null),
        },
      })
    );
  }

  // Only a draft has the short edit lock: an ordinary one, or an intake one for
  // its taker (there it only carries their activity to the idle timeout; the
  // server never locks it, and lockState hides the service's state for intake).
  private holdsLock(data: RXForm): boolean {
    return (
      data.statusCode == "DRAFT" &&
      (!data.intake ||
        data.intake.takerUserId === this.authService.currentUser.userId)
    );
  }

  postFormLoad() {
    if (this.readOnly && this.form.pdf.length) {
      let pdf = this.form.pdf.find((p) => p.lang == this.form.rxDefaultLang);
      this.pdf = pdf ?? this.form.pdf[0];
    } else {
      this.pdf = null;
    }

    if (this.form.doctor) {
      let d = this.doctorList.find(
        (r) => r.id.toString() == this.form.doctor.id.toString()
      );
      this.form.doctor = d;
    }

    if (this.form.clinic) {
      let c = this.clinicList.find(
        (r) => r.id.toString() == this.form.clinic.id.toString()
      );
      this.form.clinic = c;
    }

    this.teethEditCmp?.initFullRXTeethDisplay();
  }

  deleteForm() {
    let validationStr = this.translateService.instant(
      "translate.rx.deleteFormValidation"
    );

    this.sharedService.emitPopupchReceivedEvent({
      title: "translate.alert.infoTitle",
      icon: "exclamation-triangle",
      subTitle: validationStr,
      keep: false,
      approve: () => {
        if (this.currentOrder.statusCode == "DRAFT") {
          // cancel order
          this.broshService.cancelOrder(this.currentOrder.orderId).subscribe(
            (data) => {
              this.saving = false;
              this.router.navigate([new RouteEnum().URL_DASHBOARD]);
            },
            () => (this.saving = false)
          );
        } else {
          // delete rx
          this.save("DELETE");
        }
      },
    });
  }

  async save(updStatus: string = null, thenGiveBack: boolean = false) {
    this.saving = true;

    this.teethEditCmp?.clearSelection();
    if (this.drawAreaCmp)
      this.form.canvasDataUrl = await this.drawAreaCmp?.toDataUrl();

    // the teeth image goes with the signing, so the server can make the RX PDF itself once Meimad's number arrives
    if (updStatus == "SIGNED") {
      this.form.teethDataUrl = await this.teethEditCmp?.toDataUrl();
    }

    let scanType = this.scanOpt?.options.find(
      (o) => o.id == "ANALOG_DIGITAL_TYPE"
    );
    let publicId = this.scanOpt?.options.find((o) => o.id == "PUBLIC_ID");
    let scanner = this.scanOpt?.options.find((o) => o.id == "SCANNER");
    let scanId = this.scanOpt?.options.find((o) => o.id == "SCAN_ID");
    if (scanType?.selectedValue.id == "DIGITAL" && publicId) {
      publicId.resetValue();
    } else if (scanType?.selectedValue.id == "ANALOG" && scanner && scanId) {
      scanId.resetValue();
      scanner.resetValue();
    }

    this.broshService
      .updRXForm(this.currentOrder.orderId, this.form, updStatus)
      .subscribe(
        async (data) => {
          // refused while the Meimad sync is pending (the service said so): show the form as it is now
          if (!data && updStatus != "DELETE") {
            this.saving = false;
            this.getFormData();
            return;
          }

          let idx = this.currentOrder.filesRX.findIndex(
            (f) => f.formId == this.form.formId
          );

          if (updStatus == "DELETE") {
            if (idx != -1) {
              this.currentOrder.filesRX.splice(idx, 1);
            }
            this.rxLock.stop();
            this.reloadOrderHeader(true);
            return;
          } else {
            if (idx != -1) {
              this.currentOrder.filesRX[idx].statusCode = data.statusCode;
            }
          }

          this.dirty = false;
          this.saving = false;
          this.savedRxLock(data, updStatus == "DRAFT");

          this.postFormLoad();

          if (data.validationErrors.length) {
            this.sharedService.emitPopupchReceivedEvent({
              title: "translate.alert.infoTitle",
              icon: "exclamation-triangle",
              subTitle: data.validationErrors,
              keep: false,
            });
          } else if (thenGiveBack) {
            this.doGiveBack();
          } else if (
            updStatus == "SIGNED" &&
            (this.form.statusCode == "SIGNED" ||
              this.form.statusCode == "FINAL")
          ) {
            // A LAB RX of an order Meimad has not numbered yet gets its PDF from the server once the number arrives
            // (an RX_PDF push); asked now, its header could not carry the number. The signing's reply says so
            // (pdfAfterMmd). An older server's reply has no pdfAfterMmd and never makes the PDF later, so the PDF
            // is asked for as before.
            if (!data.pdfAfterMmd) {
              let teethDataUrl = await this.teethEditCmp?.toDataUrl();
              this.broshService
                .createRXPdf(this.form, teethDataUrl)
                .subscribe((data) => {
                  this.getFormData();
                });
            }

            if (this.form.type == "LAB") {
              if (data.showOrderLines) {
                this.orderLines = data.orderLines;
                this.showOrderLines = true;
              } else {
                this.reloadOrderHeader(false);
              }
            } else {
              this.store.dispatch(RELOAD_INBOX());
              this.back();
            }
          } else if (this.currentOrder.statusCode == "DRAFT") {
            this.router.navigate([new RouteEnum().URL_DASHBOARD]);
          }
        },
        () => (this.saving = false)
      );
  }

  // The lines are saved and the order sent to Meimad: the form follows (now pending the Meimad sync, its notice on).
  onOrderLinesSaved() {
    this.showOrderLines = false;
    this.getFormData();
    this.reloadOrderHeader(true);
  }

  onLinesChange() {
    this.broshService.updRXForm(this.currentOrder.orderId, this.form).subscribe(
      (data) => {
        if (!data) {
          // refused while the Meimad sync is pending: show the form as it is now
          this.saving = false;
          this.getFormData();
          return;
        }
        this.saving = false;
        this.dirty = false;
        this.savedRxLock(data);

        this.postFormLoad();
      },
      () => (this.saving = false)
    );
  }

  // After a save: a signed form takes no lock, so it stops renewing; a signed
  // form reopened for editing is opened as a draft; any other draft keeps its
  // lock with the date we saved (so a retake does not read our own save as
  // someone else's change).
  private savedRxLock(data: RXForm, reopened: boolean = false): void {
    if (data.statusCode != "DRAFT") {
      this.rxLock.stop();
    } else if (reopened && this.holdsLock(data)) {
      this.rxLock.start(data.formId, data.updDate as any);
    } else {
      this.rxLock.markSaved(data.updDate as any);
    }
  }

  back() {
    this.location.back();
  }

  giveBack(): void {
    if (this.dirty) {
      this.displayGiveBackPopup = true;
    } else {
      this.doGiveBack();
    }
  }

  saveAndGiveBack(): void {
    this.displayGiveBackPopup = false;
    this.save(null, true);
  }

  discardAndGiveBack(): void {
    this.displayGiveBackPopup = false;
    this.dirty = false;
    this.doGiveBack();
  }

  private doGiveBack(): void {
    this.saving = true;
    this.givingBack = true;
    this.broshService.giveBackIntakeCase(this.currentOrder.orderId).subscribe(
      () => {
        this.saving = false;
        this.rxLock.stop();
        this.patchHeaderIntake(null);
        this.store.dispatch(MARK_INBOX_NEEDS_RELOAD());
        this.router.navigate([new RouteEnum().URL_INBOX]);
      },
      () => {
        this.saving = false;
        this.givingBack = false;
        // The case may have just been freed (idle timeout): show what it is now.
        this.getFormData();
      }
    );
  }

  clearNote(note: RXNote) {
    this.form.notes.splice(this.form.notes.indexOf(note), 1);
    this.dirty = true;
  }

  onFixedNoteClose(notes: RXNote[]) {
    this.form.notes.push(...notes);
    if (notes.length) {
      this.dirty = true;
    }
    this.addFixedNoteOpenSw = false;
  }

  onShadeValueChange(value: RXOptValue) {
    if (
      !this.shade2.selectedValue ||
      !this.shade2.valueList.find((s) => s.id == this.shade2.selectedValue.id)
    ) {
      this.shade2.selectedValue = value;
    }

    if (
      !this.shade3.selectedValue ||
      !this.shade3.valueList.find((s) => s.id == this.shade3.selectedValue.id)
    ) {
      this.shade3.selectedValue = value;
    }
  }

  reloadOrderHeader(goBack: boolean) {
    if (!this.currentOrder) return;
    this.broshService.getOrderHeaderSingle(this.currentOrder.orderId, this.qcModeSw).subscribe(
      (data) => {
        if (data) {
          this.store.dispatch(
            RELOAD_CURRENT_ORDER({
              order: data,
            })
          );
        }
        if (goBack) this.back();
      },
      (error) => {
        this.sharedService.alertError(error);
      }
    );
  }

  // The Import PO button shows only for an order that can take Shila POs now.
  loadShilaPoEligibility(): void {
    if (!this.allowOrderActions || !this.currentOrder) {
      this.shilaPoEligible = false;
      return;
    }
    const orderId = this.currentOrder.orderId;
    this.broshService.getShilaPos(orderId).subscribe((list) => {
      this.shilaPoEligible = list.eligible;
      this.noticeShilaPosLeavingTheVault(orderId, list.imported ?? []);
    });
  }

  // A PO that was waiting to leave Meimad's file and now has: tell the user once.
  private noticeShilaPosLeavingTheVault(orderId: number, imported: { poNumber: string; status: string }[]): void {
    if (this.reservedShilaPosOrderId !== orderId) {
      this.reservedShilaPosOrderId = orderId;
      this.reservedShilaPos = new Set();
    }
    if (imported.some((po) => po.status === "CONSUMED_BY_BROSH" && this.reservedShilaPos.has(po.poNumber))) {
      this.toaster.info(this.translateService.instant("translate.shilaPo.leftVault"));
    }
    this.reservedShilaPos = new Set(imported.filter((po) => po.status === "RESERVED").map((po) => po.poNumber));
  }

  openIntakeParty(): void {
    const p = this.form.intake?.party;
    if (p?.needsClinic || p?.needsDentist) {
      this.displayIntakePartyPopup = true;
    } else if (p?.shilaDentist) {
      this.displayShilaPoPopup = true;
    }
  }

  // A taken case whose clinic and dentist are known still offers its possible duplicates: the popup opens on them
  // alone, unless this browser was told "not a duplicate" for the order. Only an imported case awaiting intake.
  private offerIntakeDuplicates(): void {
    const orderId = this.currentOrder.orderId;
    if (
      !this.intakeMine ||
      !this.form.intake.party ||
      this.intakePartyAsks ||
      this.intakePartyClosedFor === orderId ||
      this.intakeDuplicatesCheckedFor === orderId ||
      this.displayIntakePartyPopup ||
      this.displayShilaPoPopup ||
      isDuplicatesPassed(orderId)
    ) {
      return;
    }
    this.intakeDuplicatesCheckedFor = orderId;
    this.broshService.getIntakeParty(orderId).subscribe(
      (data) => {
        if (
          data.duplicates?.length &&
          this.currentOrder?.orderId === orderId &&
          this.intakeMine &&
          this.intakePartyClosedFor !== orderId &&
          !this.displayIntakePartyPopup &&
          !this.displayShilaPoPopup
        ) {
          this.displayIntakePartyPopup = true;
        }
      },
      () => {
        // postIntakeService shows the server's message; the form stays as it is, and the look is not retried
      }
    );
  }

  closeIntakeParty(): void {
    this.displayIntakePartyPopup = false;
    this.intakePartyClosedFor = this.currentOrder.orderId;
  }

  // The PO dialog also answers the Shila dentist question, so closing it counts as closing the popup.
  closeShilaPo(): void {
    this.displayShilaPoPopup = false;
    this.intakePartyClosedFor = this.currentOrder.orderId;
  }

  onIntakePartyChanged(): void {
    this.reloadOrderHeader(false);
    this.loadShilaPoEligibility();
  }

  onIntakePartyFinished(result: 'DONE' | 'SHILA_DENTIST'): void {
    this.closeIntakeParty();
    this.reloadFormKeepingEdits();
    if (result === 'SHILA_DENTIST') {
      this.displayShilaPoPopup = true;
    }
  }

  onShilaPosChanged(): void {
    this.reloadOrderHeader(false);
    this.loadShilaPoEligibility();
    // Answering the Shila dentist question sends no push: an intake form follows it here,
    // or, with unsaved edits, only its intake flags.
    if (this.form.intake) {
      if (this.dirty) {
        this.refreshIntakeParty();
      } else {
        this.getFormData();
      }
    }
  }

  // Unsaved edits stay; only the header and the intake flags follow.
  private reloadFormKeepingEdits(): void {
    if (this.dirty) {
      this.reloadOrderHeader(false);
      this.refreshIntakeParty();
    } else {
      this.getFormData();
    }
  }

  // The intake flags alone, from the popup's own read, so the notice beside the
  // title follows; the form and its unsaved edits stay as they are.
  private refreshIntakeParty(): void {
    if (!this.form.intake || !this.currentOrder) return;
    const orderId = this.currentOrder.orderId;
    this.broshService.getIntakeParty(orderId).subscribe(
      (data) => {
        if (this.form.intake && this.currentOrder?.orderId === orderId) {
          this.form.intake = { ...this.form.intake, party: data.party ?? null };
        }
      },
      (error) => this.sharedService.alertError(error)
    );
  }

  onFileUploaded() {
    this.reloadOrderHeader(false);
  }

  signEnabled() {
    if (this.form.type == "CLINIC") {
      let publicId = this.scanOpt?.options.find((o) => o.id == "PUBLIC_ID");
      let scanner = this.scanOpt?.options.find((o) => o.id == "SCANNER");
      let scanId = this.scanOpt?.options.find((o) => o.id == "SCAN_ID");
      let scanType = this.scanOpt?.options.find(
        (o) => o.id == "ANALOG_DIGITAL_TYPE"
      );

      let validDoctor = !this.displayDoctor || this.form.doctor;
      let validClinic = !this.displayClinic || this.form.clinic;
      let validPatient = this.form.patientDescr;
      let validLines = this.form.lines.length > 0;
      let validId =
        (scanType.selectedValue.id == "ANALOG" && publicId.hasValue()) ||
        (scanType.selectedValue.id == "DIGITAL" &&
          scanId.hasValue() &&
          scanner.hasValue());

      return validDoctor && validClinic && validId && validPatient && validLines;
    } else {
      return true;
    }
  }

  approve() {
    this.saving = true;
    this.broshService.approveRx(this.form.formId).subscribe(
      (labFormId) => {
        this.saving = false;
        this.reloadOrderHeader(false);

        let form = new RXForm();
        form.formId = labFormId;
        this.store.dispatch(
          SELECT_RX_FORM({
            form: form,
          })
        );
        this.getFormData();
      },
      () => (this.saving = false)
    );
  }

  cancelDuplicateOrder() {
    this.displayMergeRxPopup = false

    this.broshService.cancelDuplicateOrder(
      this.form.formId, this.mergeOrderIdMmd).subscribe(
        (newOrderId) => this.openMergedIntoOrder(newOrderId)
      )
  }

  // The taker confirmed in the intake popup that the case duplicates this Meimad order. The call is made here, not in
  // the popup, so the kept order opens as after the merge above even when the popup was closed meanwhile.
  onIntakePartyMerge(orderIdMmd: number): void {
    this.intakeMerging = true;
    this.broshService.cancelDuplicateOrder(this.form.intake.formId, orderIdMmd).subscribe(
      (newOrderId) => {
        this.intakeMerging = false;
        this.closeIntakeParty();
        this.openMergedIntoOrder(newOrderId);
      },
      // the service shows the server's message; the popup stays for another choice
      () => (this.intakeMerging = false)
    );
  }

  // After a merge: the order kept, in place of the one merged away.
  private openMergedIntoOrder(newOrderId: number): void {
    const routeEnum = new RouteEnum();

    this.store.dispatch(
      UI__ROUTE_NAVIGATION({
        data: { link: routeEnum.URL_ORDER },
      })
    );
    this.router.navigate([routeEnum.URL_ORDER]);

    this.broshService.getOrderHeaderSingle(newOrderId).subscribe(
      (data) => {
        if (data) {
          this.store.dispatch(
            SELECT_INBOX_ORDER({
              order: data
            })
          );
        }
      },
    )
  }

  displayScanOpt(rxOpt: RXOpt) {
    if (rxOpt.id == "ANALOG_DIGITAL_TYPE") return true;

    let scanType = this.scanOpt?.options.find(
      (o) => o.id == "ANALOG_DIGITAL_TYPE"
    );
    if (rxOpt.id == "SCAN_ID" || rxOpt.id == "SCANNER") {
      return scanType.selectedValue.id == "DIGITAL";
    } else if (rxOpt.id == "PUBLIC_ID") {
      return scanType.selectedValue.id == "ANALOG";
    }
  }

  onDrChange() {
    this.onLinesChange();
  }
}
